import React, { useState } from 'react';
interface CompanySummaryProps {
  companyProfile: {
    industry: string;
    businessSize: string;
    customerType: string;
    positioning: string;
    summary: string;
    productsServices?: string;
    targetAudience?: string;
    geographicFocus?: string;
    companyAge?: string;
    keyCompetitors?: string;
    businessModel?: string;
    marketingApproach?: string;
    technologyStack?: string;
    businessChallenges?: string;
    growthOpportunities?: string;
  };
  challenge: string;
  target: string;
}
export const CompanySummary: React.FC<CompanySummaryProps> = ({
  companyProfile,
  challenge,
  target
}) => {
  // Define which fields to show in the main grid
  const mainFields = [{
    label: 'Industry',
    key: 'industry'
  }, {
    label: 'Business Size',
    key: 'businessSize'
  }, {
    label: 'Customer Type',
    key: 'customerType'
  }, {
    label: 'Positioning',
    key: 'positioning'
  }];
  // Define which fields to show in the expanded details (if available)
  const extraFields = [{
    label: 'Products/Services',
    key: 'productsServices'
  }, {
    label: 'Target Audience',
    key: 'targetAudience'
  }, {
    label: 'Geographic Focus',
    key: 'geographicFocus'
  }, {
    label: 'Company Age',
    key: 'companyAge'
  }, {
    label: 'Business Model',
    key: 'businessModel'
  }];
  // Define advanced fields for detailed analysis
  const advancedFields = [{
    label: 'Key Competitors',
    key: 'keyCompetitors'
  }, {
    label: 'Marketing Approach',
    key: 'marketingApproach'
  }, {
    label: 'Technology Stack',
    key: 'technologyStack'
  }, {
    label: 'Business Challenges',
    key: 'businessChallenges'
  }, {
    label: 'Growth Opportunities',
    key: 'growthOpportunities'
  }];
  // Check if we have any extra fields with data
  const hasExtraFields = extraFields.some(field => companyProfile[field.key as keyof typeof companyProfile] && companyProfile[field.key as keyof typeof companyProfile] !== 'Not available');
  // Check if we have any advanced fields with data
  const hasAdvancedFields = advancedFields.some(field => companyProfile[field.key as keyof typeof companyProfile] && companyProfile[field.key as keyof typeof companyProfile] !== 'Not available');
  const [showAllDetails, setShowAllDetails] = useState(false);
  return <div className="bg-white rounded-lg border shadow-sm overflow-hidden" data-id="element-201">
      <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white" data-id="element-202">
        <h3 className="font-bold" data-id="element-203">Company Profile</h3>
      </div>
      <div className="p-6" data-id="element-204">
        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-205">
          {mainFields.map(field => <div key={field.key} data-id="element-206">
              <h4 className="text-sm font-medium text-gray-500" data-id="element-207">
                {field.label}
              </h4>
              <p className="font-medium text-gray-800" data-id="element-208">
                {companyProfile[field.key as keyof typeof companyProfile] || 'Not available'}
              </p>
            </div>)}
        </div>
        {hasExtraFields && <div className="grid grid-cols-2 gap-4 mb-4 pt-4 border-t" data-id="element-209">
            {extraFields.map(field => {
          const value = companyProfile[field.key as keyof typeof companyProfile];
          if (!value || value === 'Not available') return null;
          return <div key={field.key} data-id="element-210">
                  <h4 className="text-sm font-medium text-gray-500" data-id="element-211">
                    {field.label}
                  </h4>
                  <p className="font-medium text-gray-800" data-id="element-212">{value}</p>
                </div>;
        })}
          </div>}
        {hasAdvancedFields && showAllDetails && <div className="grid grid-cols-1 gap-4 mb-4 pt-4 border-t" data-id="element-213">
            {advancedFields.map(field => {
          const value = companyProfile[field.key as keyof typeof companyProfile];
          if (!value || value === 'Not available') return null;
          return <div key={field.key} data-id="element-214">
                  <h4 className="text-sm font-medium text-gray-500" data-id="element-215">
                    {field.label}
                  </h4>
                  <p className="font-medium text-gray-800" data-id="element-216">{value}</p>
                </div>;
        })}
          </div>}
        {hasAdvancedFields && <div className="mt-2 mb-4" data-id="element-217">
            <button onClick={() => setShowAllDetails(!showAllDetails)} className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-218">
              {showAllDetails ? 'Show Less Details' : 'Show More Details'}
            </button>
          </div>}
        <div className="mb-4" data-id="element-219">
          <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-220">
            Business Summary
          </h4>
          <p className="text-gray-700" data-id="element-221">{companyProfile.summary}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t" data-id="element-222">
          <div data-id="element-223">
            <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-224">
              Key Challenge
            </h4>
            <p className="text-gray-700" data-id="element-225">{challenge}</p>
          </div>
          <div data-id="element-226">
            <h4 className="text-sm font-medium text-gray-500 mb-1" data-id="element-227">
              Target Result
            </h4>
            <p className="text-gray-700" data-id="element-228">{target}</p>
          </div>
        </div>
      </div>
    </div>;
};